package com.evoapps.evoshift.ui.roster

import com.evoapps.evoshift.ui.util.monthInSentence
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.Color
import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.ui.util.monthName
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDateRangeField
import com.evoapps.evoshift.widgets.MissingYear
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.parseIsoDate
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.availability_end_date
import evoshift.composeapp.generated.resources.availability_start_date
import evoshift.composeapp.generated.resources.capacity_days
import evoshift.composeapp.generated.resources.capacity_whole_month
import evoshift.composeapp.generated.resources.date_end_before_start
import evoshift.composeapp.generated.resources.date_outside_month
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/*
 * Day ranges within a month (SH-48): the planning tools work on part of a month (e.g. days 1–15
 * or 16–24, as in the owner's sheet) while the month stays one roster.
 */

/** "Whole month" or "Days 1–15". */
@Composable
fun dayRangeLabel(range: ScheduleRange, year: Int, month: Int): String =
    if (range == monthRange(year, month)) stringResource(Res.string.capacity_whole_month)
    else stringResource(Res.string.capacity_days, range.start.day, range.endInclusive.day)

/** Why [start]–[end] is not a range of days of [year]-[month]; null when it is (or a date is still being typed). */
@Composable
fun dayRangeErrors(start: LocalDate?, end: LocalDate?, year: Int, month: Int): Pair<String?, String?> {
    val days = monthRange(year, month)
    val outside = stringResource(Res.string.date_outside_month, "${monthInSentence(month)} $year")
    fun outOf(date: LocalDate?) = date != null && (date < days.start || date > days.endInclusive)
    val startError = outside.takeIf { outOf(start) }
    val endError = when {
        outOf(end) -> outside
        start != null && end != null && end < start -> stringResource(Res.string.date_end_before_start)
        else -> null
    }
    return startError to endError
}

/** The two dates as a range of [year]-[month], or null when they are not one. */
fun validDayRange(start: LocalDate?, end: LocalDate?, year: Int, month: Int): ScheduleRange? {
    if (start == null || end == null || end < start) return null
    val days = monthRange(year, month)
    return if (start >= days.start && end <= days.endInclusive) ScheduleRange(start, end) else null
}

/** Picks days of one month: a start and an end date, checked to be in the month and in order. */
@Composable
fun DayRangeDialog(
    title: String,
    year: Int,
    month: Int,
    initial: ScheduleRange,
    confirmLabel: String,
    onConfirm: (ScheduleRange) -> Unit,
    onDismiss: () -> Unit,
    text: String? = null,
    loading: Boolean = false
) {
    var start by remember { mutableStateOf(initial.start.toString()) }
    var end by remember { mutableStateOf(initial.endInclusive.toString()) }
    val startDate = parseIsoDate(start)
    val endDate = parseIsoDate(end)
    val (startError, endError) = dayRangeErrors(startDate, endDate, year, month)
    val range = validDayRange(startDate, endDate, year, month)
    EvoDialog(
        title = title,
        subtitle = "${monthName(month)} $year",
        icon = Icons.Default.DateRange,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        confirm = DialogAction(confirmLabel, { range?.let(onConfirm) }, enabled = range != null && !loading, loading = loading)
    ) {
        text?.let { Text(it, style = MaterialTheme.typography.bodyMedium, color = Color.White.copy(alpha = 0.85f)) }
        // SH-166: typed or picked in the range picker, days of this month only.
        EvoDateRangeField(
            start = start,
            end = end,
            onStartChange = { start = it },
            onEndChange = { end = it },
            startLabel = stringResource(Res.string.availability_start_date),
            endLabel = stringResource(Res.string.availability_end_date),
            pickerTitle = title,
            allowed = monthRange(year, month).let { it.start..it.endInclusive },
            missingYear = MissingYear.Of(year),
            showErrors = true,
            startError = startError,
            endError = endError
        )
    }
}
